import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** 0–100. Only meaningful for the determinate variant. */
  value?: number;
  /** Determinate fills to `value`%; indeterminate pulses at full width. */
  variant?: "determinate" | "indeterminate";
  /** primary = amber fill, secondary = blue-grey chrome fill. */
  color?: "primary" | "secondary";
}

const fillColor = {
  primary: "bg-surface-primary",
  secondary: "bg-surface-inverse",
} as const;

function clamp(value: number | undefined): number {
  if (value === undefined) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
}

/**
 * Linear progress bar — track is `surface-disabled`, fill is `surface-primary`
 * (or `surface-inverse` for secondary) over a full-width 4px track.
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  ({ value, variant = "determinate", color = "primary", className, ...props }, ref) => {
    const indeterminate = variant === "indeterminate";
    const percent = clamp(value);
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(percent)}
        className={cn("h-1 w-full rounded-full bg-surface-disabled", className)}
        {...props}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width]",
            fillColor[color],
            indeterminate && "w-full animate-pulse",
          )}
          style={indeterminate ? undefined : { width: `${percent}%` }}
        />
      </div>
    );
  },
);
Progress.displayName = "Progress";
